import { useCallback, useMemo, useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import { useDebounce } from "@react-hook/debounce";

import { Progress, Table, Pagination, Input, Select } from "antd";
import type { TableProps, ProgressProps } from "antd";

import { useGetMentorshipProgramsPaginatedQuery } from "../../graphql/queries/getMentorshipProgramsPaginated.generated";
import { useGetMentorshipProgramsTotalsQuery } from "../../graphql/queries/getMentorshipProgramsTotals.generated";
import { MentorshipProgram } from "../../graphql/generated/graphql";
import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";

interface DataType {
  key: string;
  id: number;
  programName: string;
  created: string;
  createdISO: string;
  participants: number;
  completion: number;
}

type ColumnsType<T extends object> = TableProps<T>["columns"];

const twoColors: ProgressProps["strokeColor"] = {
  "0%": "#FB7798",
  "100%": "#EC76FF",
};

const columns: ColumnsType<DataType> = [
  {
    title: "Program Name",
    dataIndex: "programName",
    key: "programName",
    render: (item: string) => {
      return <span className="program-name-text">{item}</span>;
    },
  },
  {
    title: "Date Created",
    dataIndex: "created",
    key: "created",
    render: (item: string) => {
      return <span className="mentorship-table-text">{item}</span>;
    },
  },
  {
    title: "Participants",
    dataIndex: "participants",
    key: "participants",
    render: (item: string) => {
      return <span className="mentorship-table-text">{item}</span>;
    },
  },
  {
    title: "Completion",
    key: "completion",
    dataIndex: "completion",
    render: (item: number) => {
      return (
        <div className="completion-row">
          <span className="mentorship-table-text">{`${item}%`}</span>
          <Progress percent={item} showInfo={false} strokeColor={twoColors} />
        </div>
      );
    },
  },
];

export default function MentorshipPrograms() {
  const { loggedUserData } = useAuth();
  const [workspaceId, setWorkspaceId] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [totalMentorships, setTotalMentorships] = useState<number>(0);
  const [totalParticipants, setTotalParticipants] = useState<number>(0);

  // --- Filters ---
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    300
  );

  useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm, setDebouncedSearchTerm]);

  const [selectedProgramName, setSelectedProgramName] = useState<
    string | undefined
  >(undefined);
  const [createdAfter, setCreatedAfter] = useState<Dayjs | null>(null);

  const navigate = useNavigate();

  // Calculate offset for the query
  const offset = (currentPage - 1) * pageSize;

  // Calculate pageSize based on screen size
  const calculatePageSize = useCallback(() => {
    const availableHeight = window.innerHeight - 400; // Subtract header, totals, pagination
    const rowHeight = 55; //Approximate height of each row
    const calculatedSize = Math.floor(availableHeight / rowHeight);
    return Math.max(5, Math.min(calculatedSize, 20)); // Between 5 and 20 records
  }, []);

  // Adjust pageSize to screen size
  useEffect(() => {
    const newPageSize = calculatePageSize();
    setPageSize(newPageSize);

    const handleResize = () => {
      const newSize = calculatePageSize();
      setPageSize(newSize);
      // If we are on a page that no longer exists with the new size, return to page 1
      const maxPage = Math.ceil(totalRecords / newSize);
      if (currentPage > maxPage && maxPage > 0) {
        setCurrentPage(1);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculatePageSize, currentPage, totalRecords]);

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }
  }, [loggedUserData]);

  const handleOnCompletedGetMentorshipPrograms = useCallback((data: any) => {
    console.log("Get Mentorship Programs data: ", data);
  }, []);

  const handleOnErrorsGetMentorshipPrograms = useCallback(
    (error: ApolloError) => {
      console.log("Get Mentorship Programs error: ", error);
    },
    []
  );

  const { data: mentorshipProgramsData, loading: mentorshipProgramsLoading } =
    useGetMentorshipProgramsPaginatedQuery({
      skip: !workspaceId,
      variables: {
        workspaceId: workspaceId as never,
        offset: offset as never,
        limit: pageSize as never,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetMentorshipPrograms,
      onError: handleOnErrorsGetMentorshipPrograms,
    });

  const programNameOptions = useMemo(() => {
    const names = new Set<string>();
    (mentorshipProgramsData?.mentorshipProgram ?? []).forEach((p: any) => {
      if (p?.name) names.add(p.name);
    });
    return Array.from(names).map((n) => ({ label: n, value: n }));
  }, [mentorshipProgramsData]);

  const handleOnCompletedGetMentorshipProgramsTotals = useCallback(
    (data: any) => {
      console.log("Get Totals data: ", data);

      if (data?.mentorshipProgram && data?.mentorshipProgram.length) {
        const totals = data?.mentorshipProgram.reduce(
          (
            accumulator: {
              relationships: number;
              participantsIds: Set<string>;
            },
            currentValue: MentorshipProgram
          ) => {
            if (currentValue.mentorships && currentValue.mentorships.length) {
              const newValue =
                accumulator.relationships + currentValue.mentorships.length;
              accumulator.relationships = newValue;

              currentValue.mentorships.forEach((value) => {
                accumulator.participantsIds.add(value.menteeId);
                accumulator.participantsIds.add(value.mentorId);
              });
            }

            return accumulator;
          },
          {
            relationships: 0,
            participantsIds: new Set(),
          }
        );

        setTotalMentorships(totals.relationships);
        setTotalParticipants(totals.participantsIds.size);
      }

      // Update the total records for pagination
      if (data?.mentorshipProgram_aggregate?.aggregate?.count !== undefined) {
        setTotalRecords(data.mentorshipProgram_aggregate.aggregate.count);
      }
    },
    []
  );

  const handleOnErrorsGetMentorshipProgramsTotals = useCallback(
    (error: ApolloError) => {
      console.log("Get Totals error: ", error);
    },
    []
  );

  const {
    data: mentorshipProgramsTotalsData,
    loading: mentorshipProgramsTotalsLoading,
  } = useGetMentorshipProgramsTotalsQuery({
    skip: !workspaceId,
    variables: {
      workspaceId: workspaceId as any,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedGetMentorshipProgramsTotals,
    onError: handleOnErrorsGetMentorshipProgramsTotals,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const dataSource: DataType[] = useMemo(() => {
    const raw = mentorshipProgramsData?.mentorshipProgram ?? [];

    // map -> DataType
    let data: DataType[] = raw.map((element, index) => {
      const createdISO = element.createdAt as string;

      const participantsIds = new Set();
      if (element?.mentorships?.length) {
        element.mentorships.forEach((value) => {
          participantsIds.add(value.menteeId);
          participantsIds.add(value.mentorId);
        });
      }

      const progresses = (element.mentorshipProgramModules ?? []).flatMap(
        (m) => m.mentorshipProgramProgresses ?? []
      );

      const total = progresses.length;
      const completed = progresses.filter((p) => p.completed).length;
      const completion = total ? Math.round((completed * 100) / total) : 0;

      return {
        key: `${element.id}-${index}`,
        id: element.id,
        programName: element.name ?? "",
        created: dayjs(createdISO).format("MM/DD/YYYY"),
        createdISO,
        participants: participantsIds.size,
        completion: completion,
      };
    });

    // filters
    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      data = data.filter((row) => row.programName.toLowerCase().includes(q));
    }

    if (selectedProgramName) {
      data = data.filter((row) => row.programName === selectedProgramName);
    }

    if (createdAfter) {
      const after = createdAfter.startOf("day");
      data = data.filter((row) => dayjs(row.createdISO).isAfter(after));
    }

    return data;
  }, [
    mentorshipProgramsData,
    debouncedSearchTerm,
    selectedProgramName,
    createdAfter,
  ]);

  return (
    <Container component="main" maxWidth={false}>
      <div className="mentorship-row" style={{ justifyContent: "space-between" }}>
        <h1 className="screen-title">Programs</h1>
        <button
          className="button-new-program"
          onClick={() => navigate(routeNames.MENTORSHIP_ADD)}
        >
          New Program
        </button>
      </div>

      <div className="mentorship-row">
        <div className="total-item-container">
          <div className="total-icon-container">
            <img
              src={process.env.PUBLIC_URL + "/assets/images/programs-logo.svg"}
              className="mentorship-logo"
              alt="Programs Logo"
            />
          </div>
          <div className="total-info-container">
            <div className="total-title">PROGRAMS</div>
            <div className="total-info-row">
              <div className="total-value">
                {
                  mentorshipProgramsTotalsData?.mentorshipProgram_aggregate
                    .aggregate?.count
                }
              </div>
              <div>
                <span className="total-description">Active</span>
                <span className="total-description">
                  {mentorshipProgramsTotalsData?.mentorshipProgram_aggregate
                    .aggregate?.count &&
                  mentorshipProgramsTotalsData?.mentorshipProgram_aggregate
                    .aggregate?.count > 1
                    ? "Programs"
                    : "Program"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="total-item-container">
          <div className="total-icon-container">
            <img
              src={
                process.env.PUBLIC_URL + "/assets/images/relationships-logo.svg"
              }
              className="mentorship-logo"
              alt="Programs Logo"
            />
          </div>
          <div className="total-info-container">
            <div className="total-title" style={{ color: "#FB779C" }}>
              RELATIONSHIPS
            </div>
            <div className="total-info-row">
              <div className="total-value">{totalMentorships}</div>
              <div>
                <span className="total-description">Matched</span>
                <span className="total-description">
                  {totalMentorships && totalMentorships > 1 ? "Pairs" : "Pair"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="total-item-container">
          <div className="total-icon-container">
            <img
              src={
                process.env.PUBLIC_URL + "/assets/images/participants-logo.svg"
              }
              className="mentorship-logo"
              alt="Programs Logo"
            />
          </div>
          <div className="total-info-container">
            <div className="total-title" style={{ color: "#BF47FD" }}>
              PARTICIPANTS
            </div>
            <div className="total-info-row">
              <div className="total-value">{totalParticipants}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="filters-row"
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "1fr 1fr 1fr",
          marginBottom: 16,
        }}
      >
        {/* Search por programName (substring) */}
        <Input
          allowClear
          placeholder="Search…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Dropdown by program name */}
        <Select
          allowClear
          placeholder="Program Name"
          options={programNameOptions}
          value={selectedProgramName}
          onChange={(val) => setSelectedProgramName(val)}
        />

        {/* Date: Created after */}
        {/* <DatePicker
          allowClear
          placeholder="Date"
          value={createdAfter}
          onChange={(val) => setCreatedAfter(val)}
          style={{ width: "100%" }}
        /> */}
      </div>

      <div className="mentorship-row">
        <Table<DataType>
          columns={columns}
          pagination={false}
          dataSource={dataSource}
          loading={mentorshipProgramsLoading}
          style={{ width: "100%" }}
          rowKey={(record) => record.id}
          className="mentorship-programs-table"
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                const path = `${routeNames.MENTORSHIP_DETAILS.replace(
                  ":programId",
                  `${record.id}`
                )}`;

                navigate(path);
              },
            };
          }}
        />

        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Pagination
            current={currentPage}
            total={totalRecords}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
            simple={false}
            size="default"
          />
        </div>
      </div>
    </Container>
  );
}
